/**
 * What the backend can actually extract text from — the single client-side
 * statement of it (AIVA-6901).
 *
 * This list is NOT a policy choice made here. It mirrors
 * `apps/backend/ai/integrations/systemic_tools/document_extraction.py`
 * (`_EXTENSION_CONTENT_TYPES`), which is the pipeline every uploaded chat file
 * and every email attachment is fed through. A Python drift test
 * (`tests/test_attachment_supported_types.py`) reads BOTH files and fails if
 * they disagree, because that disagreement is the bug this replaces:
 *
 *   the picker hardcoded `[".docx", ".pdf", ".txt"]` while the backend had
 *   supported twelve content types for months. PPTX, XLSX, PNG and JPG worked
 *   end-to-end — the `accept` attribute simply hid them from the file dialog,
 *   so users could not select them and never saw an explanation either.
 *
 * If you add a format, add it in the Python module first and let the test tell
 * you to update this one.
 */

/** Extensions the extraction pipeline accepts, lowercase, dot-prefixed. */
export const SUPPORTED_UPLOAD_EXTENSIONS: readonly string[] = [
  ".pdf",
  ".docx",
  ".xlsx",
  ".pptx",
  ".txt",
  ".html",
  ".htm",
  ".jpg",
  ".jpeg",
  ".png",
  ".bmp",
  ".tif",
  ".tiff",
  ".heif",
];

const SUPPORTED_SET = new Set(SUPPORTED_UPLOAD_EXTENSIONS);

/**
 * Legacy binary Office formats. Genuinely unsupported — Azure Document
 * Intelligence cannot read them — so they get their own message telling the
 * user the one thing that actually resolves it: re-save in the modern format.
 * Matches the backend's wording in `attachment_summarize_tool.py`.
 */
const LEGACY_OFFICE_EXTENSIONS = new Set([".doc", ".xls", ".ppt"]);

/** The `accept` attribute for the file input. */
export const UPLOAD_ACCEPT_ATTRIBUTE = SUPPORTED_UPLOAD_EXTENSIONS.join(",");

/** The extension of `fileName`, lowercased and dot-prefixed, or "" if none. */
export function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot === -1 ? "" : fileName.slice(dot).toLowerCase();
}

export function isSupportedUpload(fileName: string): boolean {
  return SUPPORTED_SET.has(extensionOf(fileName));
}

/**
 * Why these files were rejected, phrased so the user can act on it.
 *
 * The old message named the three allowed formats and nothing else, which was
 * both wrong and unhelpful for the most common real case — a .doc or .xls,
 * where the fix is to re-save rather than to give up.
 */
export function unsupportedUploadMessage(rejected: readonly string[]): string {
  const legacy = rejected.filter((name) => LEGACY_OFFICE_EXTENSIONS.has(extensionOf(name)));
  if (legacy.length > 0 && legacy.length === rejected.length) {
    return (
      `${legacy.map((n) => extensionOf(n)).join(", ")} files can't be read. ` +
      "Re-save as .docx, .xlsx or .pptx and try again."
    );
  }
  const shown = rejected
    .map((name) => extensionOf(name) || name)
    .filter((ext, i, all) => all.indexOf(ext) === i)
    .join(", ");
  return `${shown} isn't supported. You can attach PDF, Word, Excel, PowerPoint, text, HTML and image files.`;
}
